import React, { useEffect, useState } from "react";
import { Accordion } from 'components/common';
import { useAppLogo } from "helpers/useAppSetting";
import { useSelector } from "react-redux";
import { formatDate } from "helpers/utils";
import PropTypes from 'prop-types';
import { VALUE_CLS, CHECK_DISPLAY_CLS, TH_CLS, TD_CLS, SECTION_BODY_CLS } from "../pifStyles";
import { useDiffMark, useRowMark, useDiffTitle } from "./PifDiffContext";
import DiffRichText from "./DiffRichText";

// Read-only checkbox display (replaces bootstrap Form.Check without onChange).
// `cls` appends a diff-highlight class when the field changed.
const DetailCheck = ({ label, checked, cls = '' }) => (
  <label className={`tw-flex tw-items-center tw-gap-2${cls}`}>
    <input
      type="checkbox"
      className={CHECK_DISPLAY_CLS}
      checked={!!checked}
      readOnly
    />
    {label && <span>{label}</span>}
  </label>
);

DetailCheck.propTypes = {
  label: PropTypes.node,
  checked: PropTypes.any,
  cls: PropTypes.string
}

const DetailGDS = ({pif}) => {
  const logo = useAppLogo();
  const {gds: initialKeys} = useSelector(state => state.crm_project_pif.initialKeys)
  const [activeKey, setActiveKey] = useState(initialKeys)

  const {project, building, adaptation} = pif || {};

  const gds = pif?.gds || {}
  const paintSystemData = gds?.paint_systems || []
  const secondaryMemberData = gds?.secondary_members || []
  const mark = useDiffMark();
  const rowMark = useRowMark();
  const p = (k) => `gds.${k}`;
  const rmark = (arr, item, index) => rowMark(`gds.${arr}[${item?.id}]`, `gds.${arr}[${index}]`);
  const vcls = (k) => `${VALUE_CLS}${mark(p(k))}`;
  // old → new tooltip for a highlighted field (inert when not changed).
  const title = useDiffTitle();
  const vtitle = (k) => title(p(k));

  useEffect(() => {
    setActiveKey(initialKeys);
  }, [initialKeys]);

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  return (
    <Accordion
      activeKeys={activeKey}
      onChange={handleAccordionSelect}
      className="tw-text-sm"
    >
      {/* 1. General Information */}
      <Accordion.Item eventKey="0" header="1. General Information">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-2">
              <img
                src={logo}
                alt="Logo Pebsteel"
                width="160"
                height="60"
                className="tw-object-contain tw-rounded-sm"
                style={{maxWidth: '100%'}}
              />
            </div>
            <div className="tw-col-span-2">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-5">
                  Quote No:
                </div>
                <div className="tw-col-span-7">
                  <span className={VALUE_CLS}>{project?.q_number}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-5">
                  P/Job No:
                </div>
                <div className="tw-col-span-7">
                  <span className={VALUE_CLS}>{building?.j_number}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-2">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  CO#:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{adaptation?.co || '00'}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  REV#:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{adaptation?.rev}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-2">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  Date:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{formatDate(adaptation?.entry_date)}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  Date:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{formatDate(adaptation?.entry_date)}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-4">
              <div className="tw-flex tw-justify-between">
                <DetailCheck
                  label="For Order Entry"
                  checked={adaptation?.type === "change_order"}
                />

                <DetailCheck
                  label="For Quotation"
                  checked={adaptation?.type === "revision"}
                />
              </div>
              <h6 className="tw-text-center tw-my-2">This sheet is applicable to entire project</h6>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mt-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  Project name:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{project?.name}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  Location:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{building?.pj_location}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-6">
                  No of all Bldgs in this Project:
                </div>
                <div className="tw-col-span-6">
                  <span className={vcls('no_of_bldgs')} title={vtitle('no_of_bldgs')}>{gds?.no_of_bldgs}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-6">
                  No. of Non Identical Bldgs in this Project:
                </div>
                <div className="tw-col-span-6">
                  <span className={vcls('no_of_non_identical_bldgs')} title={vtitle('no_of_non_identical_bldgs')}>{gds?.no_of_non_identical_bldgs}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 2. Current Contract status */}
      <Accordion.Item eventKey="1" header="2. Current Contract status">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <DetailCheck
                label="Letter of intent issued by customer on (Date)"
                checked={gds?.contract_loi}
                cls={mark(p('contract_loi'))}
              />
            </div>
            <div className="tw-col-span-6">
              <span className={vcls('contract_loi_on_date')} title={vtitle('contract_loi_on_date')}>{formatDate(gds?.contract_loi_on_date)}</span>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <DetailCheck
                label="PEB Standard Offer signed by customer on (Date)"
                checked={gds?.peb_standard_offer_signed}
                cls={mark(p('peb_standard_offer_signed'))}
              />
            </div>
            <div className="tw-col-span-6">
              <span className={vcls('peb_standard_offer_signed_date')} title={vtitle('peb_standard_offer_signed_date')}>{formatDate(gds?.peb_standard_offer_signed_date)}</span>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <DetailCheck
                label="Customer contract signed by customer on (Date)"
                checked={gds?.customer_contract_signed}
                cls={mark(p('customer_contract_signed'))}
              />
            </div>
            <div className="tw-col-span-6">
              <span className={vcls('customer_contract_signed_date')} title={vtitle('customer_contract_signed_date')}>{formatDate(gds?.customer_contract_signed_date)}</span>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <DetailCheck
                label="Others (Explain)"
                checked={gds?.contract_status_others}
                cls={mark(p('contract_status_others'))}
              />
            </div>
            <div className="tw-col-span-6">
              <DiffRichText path={p('contract_status_others_explain')} html={gds?.contract_status_others_explain} className={VALUE_CLS} />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 3. Penalty */}
      <Accordion.Item eventKey="2" header="3. Penalty">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Terms of penalty
            </div>
            <div className="tw-col-span-8">
              <DiffRichText path={p('term_of_penalty')} html={gds?.term_of_penalty} className={VALUE_CLS} />
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-mt-3">
            <div className="tw-col-span-3">
              Max. Penalty as % of Contract Price:
            </div>
            <div className="tw-col-span-3">
              <DetailCheck
                label="5%"
                checked={gds?.penalty_of_contact_price_5_percent}
                cls={mark(p('penalty_of_contact_price_5_percent'))}
              />
            </div>
            <div className="tw-col-span-6">
              <DetailCheck
                label="Others (Specify)"
                checked={gds?.penalty_of_contact_price_other}
                cls={mark(p('penalty_of_contact_price_other'))}
              />
              <DiffRichText path={p('penalty_of_contact_price_other_specify')} html={gds?.penalty_of_contact_price_other_specify} className={VALUE_CLS} />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 4. Shipment */}
      <Accordion.Item eventKey="3" header="4. Shipment">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Delivery Commitment (weeks):
            </div>
            <div className="tw-col-span-8">
              <span className={vcls('shipment_delivery_commitment')} title={vtitle('shipment_delivery_commitment')}>{gds?.shipment_delivery_commitment}</span>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Delivery by:
            </div>
            <div className="tw-col-span-8">
              <div className="tw-flex tw-justify-between">
                <DetailCheck
                  label="by PEB Steel"
                  checked={gds?.shipment_delivery_by_pebsteel}
                  cls={mark(p('shipment_delivery_by_pebsteel'))}
                />

                <DetailCheck
                  label="Customer pickup"
                  checked={gds?.shipment_customer_pickup}
                  cls={mark(p('shipment_customer_pickup'))}
                />

                <DetailCheck
                  label="Partial shipment allowed"
                  checked={gds.shipment_partial_allow}
                  cls={mark(p('shipment_partial_allow'))}
                />
              </div>
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12">
                <div className="tw-col-span-3">
                  <DetailCheck
                    label="Overland to"
                    checked={gds.overland_to}
                  />
                </div>
                <div className="tw-col-span-9">
                  <span className={vcls('shipment_overland_to')} title={vtitle('shipment_overland_to')}>{gds.shipment_overland_to}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12">
                <div className="tw-col-span-3">
                  <DetailCheck
                    label="Seafreight to"
                    checked={gds.seafreight_to}
                  />
                </div>
                <div className="tw-col-span-9">
                  <span className={vcls('shipment_seafreight_to')} title={vtitle('shipment_seafreight_to')}>{gds.shipment_seafreight_to}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">
                  <DetailCheck
                    label="Seafreight"
                    checked={gds.shipment_seafreight}
                    cls={mark(p('shipment_seafreight'))}
                  />
                </div>
                <div className="tw-flex-1">
                  <DetailCheck
                    label="Container"
                    checked={gds.shipment_container}
                    cls={mark(p('shipment_container'))}
                  />
                </div>
                <div className="tw-flex-1">
                  <DetailCheck
                    label="Truck"
                    checked={gds.shipment_truck}
                    cls={mark(p('shipment_truck'))}
                  />
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                <div className="tw-col-span-4">
                  Export document by:
                </div>
                <div className="tw-col-span-8">
                  <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                    <div className="tw-col-span-4">
                      <DetailCheck
                        label="PEB Steel"
                        checked={gds.shipment_export_document_by_pebsteel}
                        cls={mark(p('shipment_export_document_by_pebsteel'))}
                      />
                    </div>
                    <div className="tw-col-span-8">
                      <DetailCheck
                        label="by others"
                        checked={gds.shipment_export_document_by_others}
                        cls={mark(p('shipment_export_document_by_others'))}
                      />

                      <span className={vcls('shipment_export_document_by_others_specify')} title={vtitle('shipment_export_document_by_others_specify')}>{gds.shipment_export_document_by_others_specify}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 5. Required Initial Engineering Submittals */}
      <Accordion.Item eventKey="4" header="5. Required Initial Engineering Submittals">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6 tw-space-y-1">
              <DetailCheck
                label="Design calculation"
                checked={gds.eng_design_calculation}
                cls={mark(p('eng_design_calculation'))}
              />

              <DetailCheck
                label="Approval drawings"
                checked={gds.eng_approval_drawings}
                cls={mark(p('eng_approval_drawings'))}
              />

              <DetailCheck
                label="Erection drawings"
                checked={gds.eng_erection_drawings}
                cls={mark(p('eng_erection_drawings'))}
              />
            </div>
            <div className="tw-col-span-6 tw-space-y-1">
              <DetailCheck
                label="Preliminary Design Sheet"
                checked={gds.end_preliminary_design_sheet}
                cls={mark(p('end_preliminary_design_sheet'))}
              />

              <DetailCheck
                label="Anchor Bolts Plan"
                checked={gds.eng_anchor_bolts_plan}
                cls={mark(p('eng_anchor_bolts_plan'))}
              />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 6. Anchor Bolts */}
      <Accordion.Item eventKey="5" header="6. Anchor Bolts">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Anchor Bolts by:
            </div>
            <div className="tw-col-span-8">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                <div className="tw-col-span-6">
                  <DetailCheck
                    label="PEB Steel"
                    checked={gds.anchor_bolts_by_pebsteel}
                    cls={mark(p('anchor_bolts_by_pebsteel'))}
                  />
                </div>
                <div className="tw-col-span-6">
                  <DetailCheck
                    label="by others"
                    checked={gds.anchor_bolts_by_other}
                    cls={mark(p('anchor_bolts_by_other'))}
                  />

                  <DiffRichText path={p('anchor_bolts_by_other_specify')} html={gds.anchor_bolts_by_other_specify} className={VALUE_CLS} />
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mt-3">
            <div className="tw-col-span-6">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">Ship Anchor Bolts:</div>
                <div className="tw-flex-1">
                  <DetailCheck
                    label="with Job"
                    checked={gds.anchor_bolts_ship_with_job}
                    cls={mark(p('anchor_bolts_ship_with_job'))}
                  />
                </div>
                <div className="tw-flex-1">
                  <DetailCheck
                    label="Priod to Job"
                    checked={gds.anchor_bolts_priod_to_job}
                    cls={mark(p('anchor_bolts_priod_to_job'))}
                  />
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">Ship by:</div>
                <div className="tw-flex-1">
                  <DetailCheck
                    label="Overland"
                    checked={gds.anchor_bolts_ship_by_overland}
                    cls={mark(p('anchor_bolts_ship_by_overland'))}
                  />
                </div>
                <div className="tw-flex-1">
                  <DetailCheck
                    label="Seafreight"
                    checked={gds.anchor_bolts_ship_by_seafreight}
                    cls={mark(p('anchor_bolts_ship_by_seafreight'))}
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <DiffRichText path={p('anchor_bolts_note')} html={gds.anchor_bolts_note} className={VALUE_CLS} />
          </div>
        </div>
      </Accordion.Item>

      {/* 7. Buyer (Invoice to) */}
      <Accordion.Item eventKey="6" header="7. Buyer (Invoice to)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Phone:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}></div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Address:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}></div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Contact person:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}></div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Email:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}></div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Fax:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}></div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Title:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}></div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 8. Consignee (Ship To) */}
      <Accordion.Item eventKey="7" header="8. Consignee (Ship To)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('consignee_name')} title={vtitle('consignee_name')}>{gds.consignee_name}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Phone:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('consignee_phone')} title={vtitle('consignee_phone')}>{gds.consignee_phone}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Address:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('consignee_address')} title={vtitle('consignee_address')}>{gds.consignee_address}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Mobile:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('consignee_mobile')} title={vtitle('consignee_mobile')}>{gds.consignee_mobile}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Fax:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('consignee_fax')} title={vtitle('consignee_fax')}>{gds.consignee_fax}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Email:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('consignee_email')} title={vtitle('consignee_email')}>{gds.consignee_email}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 9. Shipment (Advice To) */}
      <Accordion.Item eventKey="8" header="9. Shipment (Advice To)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('shipment_advice_to_name')} title={vtitle('shipment_advice_to_name')}>{gds.shipment_advice_to_name}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Fax:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('shipment_advice_to_fax')} title={vtitle('shipment_advice_to_fax')}>{gds.shipment_advice_to_fax}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Mobile:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('shipment_advice_to_mobile')} title={vtitle('shipment_advice_to_mobile')}>{gds.shipment_advice_to_mobile}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Email:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('shipment_advice_to_email')} title={vtitle('shipment_advice_to_email')}>{gds.shipment_advice_to_email}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 10. Contractor */}
      <Accordion.Item eventKey="9" header="10. Contractor">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 11. Consultant */}
      <Accordion.Item eventKey="10" header="11. Consultant">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 12. Enduser */}
      <Accordion.Item eventKey="11" header="12. Enduser">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 13. Erection */}
      <Accordion.Item eventKey="12" header="13. Erection">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Erection by:
            </div>
            <div className="tw-col-span-8">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                <div className="tw-col-span-3">
                  <DetailCheck
                    label="PEB Steel"
                    checked={gds.erection_by_pebsteel}
                    cls={mark(p('erection_by_pebsteel'))}
                  />
                </div>
                <div className="tw-col-span-3">
                  <DetailCheck
                    label="by others"
                    checked={gds.erection_by_others}
                    cls={mark(p('erection_by_others'))}
                  />
                </div>
                <div className="tw-col-span-6">
                  <div className="tw-flex tw-gap-x-2">
                    <div>
                      Erector Name:
                    </div>
                    <div className="tw-flex-1">
                      <DiffRichText path={p('erector_name')} html={gds.erector_name} className={VALUE_CLS} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-1">
            <div className="tw-col-span-6">
              <DetailCheck
                label="Erection supervisior by PEB"
                checked={gds.erection_supervisor_by_peb}
                cls={mark(p('erection_supervisor_by_peb'))}
              />
            </div>
            <div className="tw-col-span-6">
              <div className="tw-flex tw-gap-x-2">
                <div>
                  Explain details:
                </div>
                <div className="tw-flex-1">
                  <DiffRichText path={p('erection_explain_detail')} html={gds.erection_explain_detail} className={VALUE_CLS} />
                </div>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-gap-x-2 tw-mt-2">
            <div>
              Note:
            </div>
            <div className="tw-flex-1">
              <DiffRichText path={p('erection_note')} html={gds.erection_note} className={VALUE_CLS} />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 14. Primary Members */}
      <Accordion.Item eventKey="13" header="14. Primary Members">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div>
              Surface treatment:
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-justify-between">
                <DetailCheck
                  label="Power clean ST3"
                  checked={gds.primary_power_clean_st3}
                  cls={mark(p('primary_power_clean_st3'))}
                />

                <DetailCheck
                  label="Shotblasting SA 2.0"
                  checked={gds.primary_shotblasting_sa_2_0}
                  cls={mark(p('primary_shotblasting_sa_2_0'))}
                />

                <DetailCheck
                  label="Shotblasting SA 2.5"
                  checked={gds.primary_shotblasting_sa_2_5}
                  cls={mark(p('primary_shotblasting_sa_2_5'))}
                />

                <DetailCheck
                  label="H.D.Galv"
                  checked={gds.primary_hd_galv}
                  cls={mark(p('primary_hd_galv'))}
                />

                <DetailCheck
                  label="DFT"
                  checked={gds.primary_dft}
                  cls={mark(p('primary_dft'))}
                />
              </div>
            </div>
          </div>
          <div className="tw-flex tw-items-center">
            <div className="tw-mr-3">Purlin Clip to Column / Rafter:</div>
            <DetailCheck checked={gds.primary_purlin_clip_to_column} cls={mark(p('primary_purlin_clip_to_column'))} />
          </div>
          <div className="tw-mt-2">
            <DiffRichText path={p('primary_memebers_note')} html={gds.primary_memebers_note} className={VALUE_CLS} />
          </div>
        </div>
      </Accordion.Item>

      {/* 15. Paint system */}
      <Accordion.Item eventKey="14" header="15. Paint system">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr>
                  <th className={TH_CLS}>Coat No.</th>
                  <th className={TH_CLS}>DFT (microns)</th>
                  <th className={TH_CLS}>Paint Type</th>
                  <th className={TH_CLS}>Manufacturer</th>
                  <th className={TH_CLS}>Color</th>
                </tr>
              </thead>
              <tbody>
                {paintSystemData && paintSystemData.map((item, index) => (
                  <tr key={index} className={rmark('paint_systems', item, index)}>
                    <td className={TD_CLS}>{item.cost_no}</td>
                    <td className={TD_CLS}>{item.dft}</td>
                    <td className={TD_CLS}>{item.paint_type}</td>
                    <td className={TD_CLS}>{item.manufacturer}</td>
                    <td className={TD_CLS}>{item.color}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-4">
            <DiffRichText path={p('paint_system_note')} html={gds?.paint_system_note} className={VALUE_CLS} />
          </div>
        </div>
      </Accordion.Item>

      {/* 16. Secondary Members (Purlins, girts, eave strut) */}
      <Accordion.Item eventKey="15" header="16. Secondary Members (Purlins, girts, eave strut)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-3">
              <DetailCheck
                label="Galv."
                checked={gds.second_galv}
                cls={mark(p('second_galv'))}
              />
            </div>
            <div className="tw-col-span-9">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                <div className="tw-col-span-3 tw-font-semibold">
                  Surface treatment:
                </div>
                <div className="tw-col-span-9">
                  <div className="tw-flex tw-justify-between">
                    <DetailCheck
                      label="Power clean ST3"
                      checked={gds.second_power_clean_st3}
                      cls={mark(p('second_power_clean_st3'))}
                    />

                    <DetailCheck
                      label="Shotblasting SA 2.0"
                      checked={gds.second_shotblasting_sa_2_0}
                      cls={mark(p('second_shotblasting_sa_2_0'))}
                    />

                    <DetailCheck
                      label="Shotblasting SA 2.5"
                      checked={gds.second_shotblasting_sa_2_5}
                      cls={mark(p('second_shotblasting_sa_2_5'))}
                    />
                  </div>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mt-2">
                <div className="tw-col-span-3 tw-font-semibold">
                  Paint system:
                </div>
                <div className="tw-col-span-9">
                  <div className="tw-flex tw-justify-between">
                    <DetailCheck
                      label="Standard Alkyd red oxide primer"
                      checked={gds.second_paint_standard}
                      cls={mark(p('second_paint_standard'))}
                    />

                    <DetailCheck
                      label="Special (see below)"
                      checked={gds.second_paint_speical}
                      cls={mark(p('second_paint_speical'))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="tw-mt-2 tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr>
                  <th className={TH_CLS}>Coat No.</th>
                  <th className={TH_CLS}>DFT (microns)</th>
                  <th className={TH_CLS}>Paint Type</th>
                  <th className={TH_CLS}>Manufacturer</th>
                  <th className={TH_CLS}>Color</th>
                </tr>
              </thead>
              <tbody>
                {secondaryMemberData && secondaryMemberData.map((item, index) => (
                  <tr key={index} className={rmark('secondary_members', item, index)}>
                    <td className={TD_CLS}>{item.cost_no}</td>
                    <td className={TD_CLS}>{item.dft}</td>
                    <td className={TD_CLS}>{item.paint_type}</td>
                    <td className={TD_CLS}>{item.manufacturer}</td>
                    <td className={TD_CLS}>{item.color}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-4">
            <DiffRichText path={p('secondary_memeber_note')} html={gds?.secondary_memeber_note} className={VALUE_CLS} />
          </div>
        </div>
      </Accordion.Item>

      {/* 17. Attached */}
      <Accordion.Item eventKey="16" header="17. Attached">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-justify-between tw-mx-4">
            <DetailCheck
              label="GDS"
              checked={gds.attached_gds}
              cls={mark(p('attached_gds'))}
            />

            <DetailCheck
              label="BDS1"
              checked={gds.attached_bds1}
              cls={mark(p('attached_bds1'))}
            />

            <DetailCheck
              label="BDS2"
              checked={gds.attached_bds2}
              cls={mark(p('attached_bds2'))}
            />

            <DetailCheck
              label="BDS3"
              checked={gds.attached_bds3}
              cls={mark(p('attached_bds3'))}
            />

            <DetailCheck
              label="Sketchs"
              checked={gds.attached_sketchs}
              cls={mark(p('attached_sketchs'))}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 18. Checking */}
      <Accordion.Item eventKey="17" header="18. Checking">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div className="tw-flex-1">
              <div className="tw-flex tw-items-center tw-gap-x-2">
                <div>Filled by:</div>
                <div className="tw-flex-1">
                  <span className={vcls('filled_by')} title={vtitle('filled_by')}>{gds.filled_by}</span>
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-items-center tw-gap-x-2">
                <div>Checked by:</div>
                <div className="tw-flex-1">
                  <span className={vcls('checked_by')} title={vtitle('checked_by')}>{gds.checked_by}</span>
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-items-center tw-gap-x-2">
                <div>Date:</div>
                <div className="tw-flex-1">
                  <span className={vcls('checked_date')} title={vtitle('checked_date')}>{formatDate(gds.checked_date)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 19. Double Side Welding - UT Test - Third Part Inspection */}
      <Accordion.Item eventKey="18" header="19. Double Side Welding - UT Test - Third Part Inspection">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div className="tw-space-y-1">
              <DetailCheck
                label="Double Side Welding"
                checked={gds.double_side_welding}
                cls={mark(p('double_side_welding'))}
              />

              <DetailCheck
                label="UT Test"
                checked={gds.ut_test}
                cls={mark(p('ut_test'))}
              />

              <DetailCheck
                label="Third Party Inspection"
                checked={gds.third_party_inspection}
                cls={mark(p('third_party_inspection'))}
              />
            </div>
            <div className="tw-flex-1">
              <span className={vcls('double_side_welding_notes')} title={vtitle('double_side_welding_notes')}>{gds.double_side_welding_notes}</span>
            </div>
          </div>

          <div className="tw-flex tw-justify-between tw-mt-3">
            <DetailCheck
              label="Engineering"
              checked={gds.engineering}
              cls={mark(p('engineering'))}
            />

            <DetailCheck
              label="Sales Support Dept,"
              checked={gds.sales_support_dept}
              cls={mark(p('sales_support_dept'))}
            />

            <DetailCheck
              label="Sales Dept,"
              checked={gds.sales_dept}
              cls={mark(p('sales_dept'))}
            />

            <DetailCheck
              label="Customer Service Dept,"
              checked={gds.customer_service_dept}
              cls={mark(p('customer_service_dept'))}
            />

            <DetailCheck
              label="Production,"
              checked={gds.production}
              cls={mark(p('production'))}
            />

            <DetailCheck
              label="Finance,"
              checked={gds.finance}
              cls={mark(p('finance'))}
            />
          </div>
        </div>
      </Accordion.Item>
    </Accordion>
  )
}

DetailGDS.propTypes = {
  pif: PropTypes.object.isRequired
}

export default DetailGDS;
